import { PageSize } from '@/constant';
import { CATEGORIES } from '@/pages/workers/pm1-03/components/franchise-info/config';
import {
	GetWorkerProgramApplicantListParams,
	GetWorkerProgramApplicantStatusResponse,
	GetWorkerProgramFranchiseListParams,
	GetWorkerProgramFranchiseStatusResponse,
	GetWorkerProgramListParams,
	IProgram,
	IWorkerProgram,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';

import { RootState } from '..';
import { AuthActions } from '../auth/auth.redux';
import { storage } from '../store.persist';
import {
	formatWorkerProgramDetail,
	formatWorkerProgramList,
} from './worker-program.helper';
import {
	IItemWorkerProgram,
	IPayloadCreateWorkerProgramApplicantRequest,
	IPayloadCreateWorkerProgramFranchiseRequest,
	IPayloadCreateWorkerProgramRequest,
	IPayloadDeleteWorkerProgramApplicantByIdRequest,
	IPayloadDeleteWorkerProgramFranchiseByIdRequest,
	IPayloadDeleteWorkerProgramRequest,
	IPayloadGetWorkerProgramAllListRequest,
	IPayloadGetWorkerProgramAllListSuccess,
	IPayloadGetWorkerProgramApplicantListRequest,
	IPayloadGetWorkerProgramApplicantListSuccess,
	IPayloadGetWorkerProgramApplicantStatusRequest,
	IPayloadGetWorkerProgramApplicantStatusSuccess,
	IPayloadGetWorkerProgramDetailRequest,
	IPayloadGetWorkerProgramDetailSuccess,
	IPayloadGetWorkerProgramExcelListRequest,
	IPayloadGetWorkerProgramFranchiseListRequest,
	IPayloadGetWorkerProgramFranchiseListSuccess,
	IPayloadGetWorkerProgramFranchiseStatusRequest,
	IPayloadGetWorkerProgramFranchiseStatusSuccess,
	IPayloadGetWorkerProgramListRequest,
	IPayloadGetWorkerProgramListSuccess,
	IWorkerProgramApplicantDetailStore,
	IWorkerProgramDetailStore,
	IWorkerProgramFranchiseDetailStore,
	IWorkerProgramListDetailStore,
} from './worker-program.type';

const InitParams: GetWorkerProgramListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
	name: null,
	programStartDate: null,
	programEndDate: null,
	registStartDate: null,
	registEndDate: null,
};

const InitApplicantParams: GetWorkerProgramApplicantListParams = {
	page: 1,
	pageSize: PageSize.S,
	name: '',
};

const InitFranchiseParams: GetWorkerProgramFranchiseListParams = {
	page: 1,
	pageSize: PageSize.S,
	employerName: '',
	typeDetail: CATEGORIES[0].value,
};

const initialState = {
	fetching: false,
	excelListFetching: false,
	workerProgramList: [] as IWorkerProgramListDetailStore[],
	total: 0,
	params: InitParams,
	workerProgramDetail: {} as IWorkerProgramDetailStore,

	workerProgramStatusList: [] as IWorkerProgramListDetailStore[],
	totalStatusList: 0,
	paramsStatusList: InitParams,

	applicantParams: InitApplicantParams,
	applicantList: [] as IWorkerProgramApplicantDetailStore[],
	totalApplicantList: 0,
	applicantStatus: [] as GetWorkerProgramApplicantStatusResponse,

	franchiseParams: InitFranchiseParams,
	franchiseList: [] as IWorkerProgramFranchiseDetailStore[],
	totalFranchiseList: 0,
	workerProgramAllList: [] as IItemWorkerProgram[],
	franchiseStatus: [] as GetWorkerProgramFranchiseStatusResponse,
};

const workerProgramSlice = createSlice({
	initialState,
	name: 'workerProgram',
	reducers: {
		/**---- GET WORKER PROGRAM LIST START ---- */
		getWorkerProgramListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getWorkerProgramListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.workerProgramList = content;
			state.total = total;
		},
		getWorkerProgramListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerProgramList = [];
		},

		getWorkerProgramExcelListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramExcelListRequest>
		) => {
			state.excelListFetching = true;
		},
		getWorkerProgramExcelListSuccess: (state) => {
			state.excelListFetching = false;
		},
		getWorkerProgramExcelListFailure: (state) => {
			state.excelListFetching = false;
		},
		/**---- GET WORKER PROGRAM LIST END ---- */

		/**---- DELETE WORKER PROGRAM START ---- */
		deleteWorkerProgramRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerProgramRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerProgramSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerProgramFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER PROGRAM START ---- */

		/**---- CREATE WORKER PROGRAM START ---- */
		createWorkerProgramRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerProgramRequest>
		) => {
			state.fetching = true;
		},
		createWorkerProgramSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerProgramFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER PROGRAM END ---- */

		/**---- GET WORKER PROGRAM DETAIL START ---- */
		getWorkerProgramDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramDetailRequest>
		) => {
			state.fetching = true;
		},
		getWorkerProgramDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramDetailSuccess>
		) => {
			const { workerProgramDetail } = action.payload;
			state.fetching = false;
			state.workerProgramDetail = workerProgramDetail;
		},
		getWorkerProgramDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER PROGRAM DETAIL END ---- */

		/**---- UPDATE WORKER PROGRAM START ---- */
		updateWorkerProgramSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IWorkerProgram>>
		) => {
			const { data } = action.payload;
			const workerProgramList = [...state.workerProgramList];
			state.workerProgramList = formatWorkerProgramList(
				mergeListItemUpdated<IWorkerProgramListDetailStore, IWorkerProgram>(
					workerProgramList,
					data
				)
			);
		},
		updateWorkerProgramDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<IProgram>>
		) => {
			const { data } = action.payload;
			const workerProgramDetail = { ...state.workerProgramDetail };
			state.workerProgramDetail = formatWorkerProgramDetail(
				mergeItemUpdated<IWorkerProgramDetailStore, IProgram>(
					workerProgramDetail,
					data
				)
			);
		},
		/**---- UPDATE WORKER PROGRAM END ---- */

		/**---- GET WORKER PROGRAM APPLICANT LIST START ---- */
		getWorkerProgramApplicantListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramApplicantListRequest>
		) => {
			const { params } = action.payload;
			return {
				...state,
				fetching: true,
				applicantParams: params,
			};
		},
		getWorkerProgramApplicantListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramApplicantListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.applicantList = content;
			state.totalApplicantList = total;
		},
		getWorkerProgramApplicantListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.applicantList = [];
		},
		/**---- GET WORKER PROGRAM APPLICANT LIST END ---- */

		/**---- DELETE WORKER PROGRAM APPLICANT BY PROGRAM ID START ---- */
		deleteWorkerProgramApplicantByIdRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerProgramApplicantByIdRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerProgramApplicantByIdSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerProgramApplicantByIdFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER PROGRAM APPLICANT BY PROGRAM ID END ---- */

		/**---- CREATE WORKER PROGRAM APPLICANT START ---- */
		createWorkerProgramApplicantRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerProgramApplicantRequest>
		) => {
			state.fetching = true;
		},
		createWorkerProgramApplicantSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerProgramApplicantFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER PROGRAM APPLICANT END ---- */

		/**---- GET WORKER PROGRAM FRANCHISE LIST START ---- */
		getWorkerProgramFranchiseListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramFranchiseListRequest>
		) => {
			const { params } = action.payload;
			return {
				...state,
				fetching: true,
				franchiseParams: params,
			};
		},
		getWorkerProgramFranchiseListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramFranchiseListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.franchiseList = content;
			state.totalFranchiseList = total;
		},
		getWorkerProgramFranchiseListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.franchiseList = [];
		},
		/**---- GET WORKER PROGRAM FRANCHISE LIST END ---- */

		/**---- DELETE WORKER PROGRAM APPLICANT BY PROGRAM ID START ---- */
		deleteWorkerProgramFranchiseByIdRequest: (
			state,
			action: PayloadAction<IPayloadDeleteWorkerProgramFranchiseByIdRequest>
		) => {
			state.fetching = true;
		},
		deleteWorkerProgramFranchiseByIdSuccess: (state) => {
			state.fetching = false;
		},
		deleteWorkerProgramFranchiseByIdFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE WORKER PROGRAM APPLICANT BY PROGRAM ID END ---- */

		/**---- CREATE WORKER PROGRAM START ---- */
		createWorkerProgramFranchiseRequest: (
			state,
			action: PayloadAction<IPayloadCreateWorkerProgramFranchiseRequest>
		) => {
			state.fetching = true;
		},
		createWorkerProgramFranchiseSuccess: (state) => {
			state.fetching = false;
		},
		createWorkerProgramFranchiseFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE WORKER PROGRAM END ---- */

		/**---- GET ALL WORKER PROGRAM LIST START ---- */
		getWorkerProgramAllListRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramAllListRequest | undefined>
		) => {
			return {
				...state,
				fetching: true,
			};
		},
		getWorkerProgramAllListSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramAllListSuccess>
		) => {
			const { content } = action.payload;
			state.fetching = false;
			state.workerProgramAllList = content;
		},
		getWorkerProgramAllListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.workerProgramAllList = [];
		},

		/**---- GET ALL WORKER PROGRAM LIST END ---- */

		/**---- GET WORKER PROGRAM APPLICANT STATUS START ---- */
		getWorkerProgramApplicantStatusRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramApplicantStatusRequest>
		) => {
			state.fetching = true;
		},
		getWorkerProgramApplicantStatusSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramApplicantStatusSuccess>
		) => {
			const { content } = action.payload;
			state.fetching = false;
			state.applicantStatus = content;
		},
		getWorkerProgramApplicantStatusFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET WORKER PROGRAM APPLICANT STATUS END ---- */

		/**---- GET WORKER PROGRAM FRANCHISE STATUS START ---- */
		getWorkerProgramFranchiseStatusRequest: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramFranchiseStatusRequest>
		) => {
			state.fetching = true;
		},
		getWorkerProgramFranchiseStatusSuccess: (
			state,
			action: PayloadAction<IPayloadGetWorkerProgramFranchiseStatusSuccess>
		) => {
			const { content } = action.payload;
			state.fetching = false;
			state.franchiseStatus = content;
		},
		getWorkerProgramFranchiseStatusFailure: (state) => {
			state.fetching = false;
		},
		resetWorkerProgramFranchise: (state) => {
			state.franchiseStatus = [];
		},
		/**---- GET WORKER PROGRAM FRANCHISE STATUS END ---- */
	},
	extraReducers(builder) {
		builder.addCase(AuthActions.logoutSuccess, (state) => {
			return initialState;
		});
	},
});

export const WorkerProgramSelectors = {
	selectFetching: (state: RootState) => state.workerProgram.fetching,
	selectParams: (state: RootState) => state.workerProgram.params,
	selectList: (state: RootState) => state.workerProgram.workerProgramList,
	selectTotal: (state: RootState) => state.workerProgram.total,
	selectDetail: (state: RootState) => state.workerProgram.workerProgramDetail,

	selectApplicantTotal: (state: RootState) =>
		state.workerProgram.totalApplicantList,
	selectApplicantListParams: (state: RootState) =>
		state.workerProgram.applicantParams,
	selectApplicantList: (state: RootState) => state.workerProgram.applicantList,

	selectFranchiseTotal: (state: RootState) =>
		state.workerProgram.totalFranchiseList,
	selectFranchiseListParams: (state: RootState) =>
		state.workerProgram.franchiseParams,
	selectFranchiseList: (state: RootState) => state.workerProgram.franchiseList,
	selectWorkerProgramAllList: (state: RootState) =>
		state.workerProgram.workerProgramAllList,
	selectApplicantStatus: (state: RootState) =>
		state.workerProgram.applicantStatus,
	selectFranchiseStatus: (state: RootState) =>
		state.workerProgram.franchiseStatus,
};

export const WorkerProgramActions = workerProgramSlice.actions;
export const WORKER_PROGRAM_PERSIST_KEY = 'workerProgramPersist';
export const WorkerProgramReducer = persistReducer(
	{
		key: WORKER_PROGRAM_PERSIST_KEY,
		storage: storage,
		whitelist: ['franchiseStatus'],
	},
	workerProgramSlice.reducer
);
